import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type FamilyScope =
  | { type: 'personal' }
  | { type: 'family'; ownerId: string; groupId: string; groupName: string };

interface AvailableFamily {
  ownerId: string;
  groupId: string;
  groupName: string;
  ownerName: string | null;
  role: string | null;
}


interface FamilyScopeContextValue {
  scope: FamilyScope;
  setScope: (scope: FamilyScope) => void;
  availableFamilies: AvailableFamily[];
  loading: boolean;
}

const FamilyScopeContext = createContext<FamilyScopeContextValue>({
  scope: { type: 'personal' },
  setScope: () => {},
  availableFamilies: [],
  loading: false,
});

const STORAGE_KEY = 'fennest:active_scope';

export function FamilyScopeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const { data: availableFamilies = [], isLoading } = useQuery({
    queryKey: ['family-scopes', user?.id],
    queryFn: async (): Promise<AvailableFamily[]> => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('family_members')
        .select('group_id, role, family_groups!inner(id, owner_id, name)')
        .eq('user_id', user.id);
      const rows = (data || []) as any[];
      // Don't surface the user's own owned family as a separate scope
      return rows
        .filter((r) => r.family_groups?.owner_id !== user.id)
        .map((r) => ({
          ownerId: r.family_groups.owner_id,
          groupId: r.family_groups.id,
          groupName: r.family_groups.name,
          ownerName: null,
          role: r.role ?? null,
        }));
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const [scope, setScopeState] = useState<FamilyScope>({ type: 'personal' });

  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY}:${user.id}`);
      if (raw) {
        const parsed = JSON.parse(raw) as FamilyScope;
        if (parsed.type === 'personal') {
          setScopeState({ type: 'personal' });
        } else if (parsed.type === 'family' && availableFamilies.some((f) => f.ownerId === parsed.ownerId)) {
          setScopeState(parsed);
        }
      }
    } catch {}
  }, [user?.id, availableFamilies]);

  const setScope = useCallback(
    (next: FamilyScope) => {
      setScopeState(next);
      if (user?.id) {
        try { localStorage.setItem(`${STORAGE_KEY}:${user.id}`, JSON.stringify(next)); } catch {}
      }
    },
    [user?.id],
  );

  const value = useMemo(
    () => ({ scope, setScope, availableFamilies, loading: isLoading }),
    [scope, setScope, availableFamilies, isLoading],
  );

  return <FamilyScopeContext.Provider value={value}>{children}</FamilyScopeContext.Provider>;
}

export function useFamilyScope() {
  return useContext(FamilyScopeContext);
}
